import { useRef } from 'react'
import {
  buildTableReferences,
  getDocumentParagraphs,
  getDocumentText,
  getFullDocumentComments,
  getSelectedHtml,
  getSelectedParagraphs,
} from '../api/word/word-service'
import { WORKING_DOCUMENT_ID } from '../constants/files'
import type { SelectedFile, SelectedSection } from '../contexts/SelectedFilesContext'
import type {
  ChatCompletionContext,
  CurrentDocumentCommentEntry,
  CurrentDocumentContextEntry,
  FilepoolContextEntry,
} from '../types'
import {
  collectTargetIds,
  mergeTableHtmlReferencesIntoEntries,
} from '../utils/word/attach-table-html-references'
import { buildWorkingDocSectionEntries } from '../utils/word/build-working-doc-section-entries'
import { normalizeToCanonical } from '../utils/html-sanitization/normalize-to-canonical'
import { useSelectedContext } from './useSelectedContext'

export const MAX_SECTIONS_FOR_HTML = 5

/**
 * Returns the ordered list of context card IDs to render in the ContextBar.
 * ContextCard components will use the IDs to look up their own data from the SelectedFilesContext.
 */
function buildContextItemIds(selectedFiles: SelectedFile[]): string[] {
  return selectedFiles
    .filter((f) => f.sections !== null)
    .sort((a, b) => {
      // Working document always comes first, then sort alphabetically by file name (case-insensitive)
      if (a.id === WORKING_DOCUMENT_ID) return -1
      if (b.id === WORKING_DOCUMENT_ID) return 1
      return a.fileName.localeCompare(b.fileName, undefined, { sensitivity: 'base' })
    })
    .map((f) => f.id)
}

function buildActiveContextCount(selectedFiles: SelectedFile[]): number {
  return selectedFiles.reduce((count, file) => {
    if (file.sections === null) return count
    // Files with specific sections selected count each section;
    // fully selected files (empty array) count as 1
    return count + (file.sections.length > 0 ? file.sections.length : 1)
  }, 0)
}

async function buildMouseSelectionEntries(
  isCommentMode: boolean
): Promise<CurrentDocumentContextEntry[]> {
  if (isCommentMode) {
    const selectedParagraphs = await getSelectedParagraphs()
    return selectedParagraphs.map((p) => ({
      type: 'mouse_selection' as const,
      id: p.id,
      content: p.text,
    }))
  }

  const selected = await getSelectedHtml()
  if (selected === null) return []

  const content = normalizeToCanonical(selected.html)
  return [{ type: 'mouse_selection', content }]
}

async function buildWorkingDocumentEntries(
  sections: SelectedSection[],
  isCommentMode: boolean
): Promise<CurrentDocumentContextEntry[]> {
  if (sections.length === 0) {
    if (isCommentMode) {
      const paragraphs = await getDocumentParagraphs()
      return paragraphs.map((paragraph) => ({
        type: 'full_document_paragraph' as const,
        id: paragraph.id,
        content: paragraph.text,
      }))
    }

    // Empty array = entire working document is selected
    const docText = await getDocumentText()
    return [{ type: 'full_document', content: docText }]
  }

  // Specific sections selected — fetch all concurrently to avoid sequential round-trips
  // null = section gone between hierarchy refresh and chat send; hierarchy hook handles cleanup
  // More than MAX_SECTIONS_FOR_HTML sections — fetch plain text to reduce payload size
  const useHtml = sections.length <= MAX_SECTIONS_FOR_HTML
  const sectionEntryGroups = await Promise.all(
    sections.map((section) =>
      buildWorkingDocSectionEntries(section, { perParagraph: isCommentMode, useHtml })
    )
  )
  return sectionEntryGroups.flat()
}

// Agent mode never inlines working-doc section content - it sends ids only and lets
// the agent pull content on demand via get_section/get_document_outline, same as filepool.
function buildAgentWorkingDocumentEntries(
  sections: SelectedSection[]
): CurrentDocumentContextEntry[] {
  if (sections.length === 0) return [{ type: 'full_document_included' }]
  return [{ type: 'section_ids', ids: sections.map((s) => s.id) }]
}

/**
 * getFullDocumentComments() reads every comment in the whole Word document - it
 * has no concept of what the user actually selected for review. This narrows
 * that down to the region of interest (ROI): only comments anchored to a
 * paragraph id that appears in current_document_context, or to a table cell id
 * covered by a table_html reference (those ids were omitted from
 * current_document_context by omitTableCellTargets, but the backend can still
 * anchor to them via table_html, so they remain in scope here too).
 *
 * A comment with no paragraph_id (its anchor paragraph was deleted) can't be
 * matched to any ROI, so it is dropped rather than guessed into scope.
 */
function scopeCommentsToRoi(
  comments: CurrentDocumentCommentEntry[],
  currentDocumentContext: CurrentDocumentContextEntry[],
  targetIdsInTables: ReadonlySet<string>
): CurrentDocumentCommentEntry[] {
  const roiParagraphIds = new Set([
    ...collectTargetIds(currentDocumentContext),
    ...targetIdsInTables,
  ])
  return comments.filter(
    (comment) => comment.paragraph_id !== undefined && roiParagraphIds.has(comment.paragraph_id)
  )
}

/**
 * Provides `buildContext()` — an async function that assembles the full
 * {@link ChatCompletionContext} to be sent with each chat completion request.
 *
 * Context is built in three steps:
 * 1. Discover sources (filepool, mouse selection, working document)
 * 2. Materialize `current_document_context` - comment mode only changes entry shape/type
 * 3. Attach `current_document_comments` whenever comment mode is on: scoped to the ROI
 *    when context is present; sent unscoped for thread-only review (empty context →
 *    review_mode "comments", which depends entirely on existing threads)
 *
 * - `filepool_context`: uploaded files selected by the user (working document never listed here)
 * - `current_document_context`: live Word document data fetched at call time:
 *   - `mouse_selection` HTML selection (normal) or paragraph `id` + plain `content` (comment mode)
 *   - `full_document` working document fully selected (normal mode)
 *   - `full_document_paragraph` working document fully selected in comment mode
 *   - `section` one or multiple sections selected by the user
 *   - `table_html`: comment mode only - one whole-table HTML reference per distinct table
 *     touched by any of the target entries above (see buildTableReferences)
 * - `isAgentMode`: replaces `section`/`full_document` with id-only `section_ids`/
 *   `full_document_included` entries, so the agent pulls content on demand
 *
 * @returns `{ buildContext, getMouseSelectionParagraphIds }` — call
 * `await buildContext()` inside a mutation to get fresh context; call
 * `getMouseSelectionParagraphIds()` right after to read the paragraph ids that same call saw
 * selected, table cells included (see below)
 */
export function useChatContext() {
  const { selectedFiles, getFileSelection } = useSelectedContext()

  const contextItemIds = buildContextItemIds(selectedFiles)
  const activeContextCount = buildActiveContextCount(selectedFiles)

  // Raw (pre-omission) mouse-selection paragraph ids from the most recent buildContext() call.
  // omitTableCellTargets strips table-cell ids out of current_document_context (their text is
  // already sent once via table_html, to avoid extra tokens) - but insertReviewComments still needs the
  // *true* first/last selected paragraph to reconstruct the selection range locally; a table
  // sitting at either edge of the selection would otherwise fall outside that range.
  const mouseSelectionParagraphIdsRef = useRef<string[]>([])

  const buildContext = async ({
    isCommentMode,
    includeComments,
    isAgentMode,
  }: {
    isCommentMode: boolean
    includeComments: boolean
    isAgentMode?: boolean
  }): Promise<ChatCompletionContext> => {
    const filepool_context: FilepoolContextEntry[] = selectedFiles
      .filter(({ id }: SelectedFile) => id !== WORKING_DOCUMENT_ID)
      .map(({ id, sections }: SelectedFile) => ({
        file_id: id,
        section_ids: sections?.map((s) => s.id) ?? [],
      }))

    const workingDocSections = getFileSelection(WORKING_DOCUMENT_ID)?.sections

    const [mouseEntries, workingDocEntries] = await Promise.all([
      buildMouseSelectionEntries(isCommentMode),
      workingDocSections == null
        ? Promise.resolve([])
        : isAgentMode
          ? Promise.resolve(buildAgentWorkingDocumentEntries(workingDocSections))
          : buildWorkingDocumentEntries(workingDocSections, isCommentMode),
    ])

    const targetEntries = [...mouseEntries, ...workingDocEntries]

    // Agent mode never uses comment mode, so the rest can be skipped
    if (isAgentMode) {
      return {
        filepool_context,
        current_document_context: targetEntries,
      }
    }

    mouseSelectionParagraphIdsRef.current = collectTargetIds(mouseEntries)

    const targetIds = collectTargetIds(targetEntries)

    const [tableReferenceResult, commentsResult] = await Promise.all([
      isCommentMode
        ? buildTableReferences(targetIds)
        : Promise.resolve({
            references: [],
            targetIdsInTables: new Set<string>(),
          }),
      includeComments
        ? getFullDocumentComments().then(
            (comments) => ({ ok: true as const, comments }),
            (error) => ({ ok: false as const, error })
          )
        : Promise.resolve({ ok: true as const, comments: [] as CurrentDocumentCommentEntry[] }),
    ])

    const {
      contextEntries: current_document_context,
      filteredTargetEntries,
      targetIdsInTables,
    } = mergeTableHtmlReferencesIntoEntries(targetEntries, tableReferenceResult)

    let current_document_comments: CurrentDocumentCommentEntry[] = []
    if (includeComments) {
      if (!commentsResult.ok) {
        console.warn('Failed to read document comments; sending without them', commentsResult.error)
      } else {
        current_document_comments =
          isCommentMode && targetEntries.length > 0
            ? scopeCommentsToRoi(commentsResult.comments, filteredTargetEntries, targetIdsInTables)
            : commentsResult.comments
      }
    }

    return { filepool_context, current_document_context, current_document_comments }
  }

  function getMouseSelectionParagraphIds() {
    return mouseSelectionParagraphIdsRef.current
  }

  return {
    buildContext,
    getMouseSelectionParagraphIds,
    contextItems: contextItemIds,
    activeContextCount,
  }
}
